import React, {useState} from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import toast, {Toaster} from 'react-hot-toast';
import { updatePassword } from './fetchUserDetails';

interface PasswordRes {
    message: string;
}

export default function ChangePassword() {
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const handlePasswordChange = (field: keyof typeof passwordData, value: string) => {
        setPasswordData(prev => ({ ...prev, [field]: value }));
    };

    const togglePasswordVisibility = (field: keyof typeof showPassword) => {
        setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
    };
    const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Validate password fields
        if (!passwordData.currentPassword) {
            toast.error("Please enter your current password");
            return;
        } if (!passwordData.newPassword) {
            toast.error("Please enter a new password");
            return;
        } if (passwordData.newPassword.length < 8) {
            toast.error("New password must be at least 8 characters long");
            return;
        } if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.error("New passwords do not match");
            return;
        } if (passwordData.currentPassword === passwordData.newPassword) {
            toast.error("New password must be different from current password");
            return;
        }

        try {
            const res = await updatePassword(passwordData) as PasswordRes;
            if (res.message === "Password created and stored successfully.") {
                toast.success("Password updated successfully");
            }else if (res.message === "Password updated successfully.") {
                toast.success("Password updated successfully");
            }else {
                toast.error(res.message);
            }
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (err: any) {
            toast.error(`Error: ${err.message || "Failed to update password"}`);
        }
    };
    return (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <Toaster />
            <div className="bg-gradient-to-r from-red-500 to-pink-500 p-8">
                <h2 className="text-3xl font-bold text-white mb-2">Change Password</h2>
                <p className="text-red-100">Update your account password for security</p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="p-8">
                <div className="grid gap-6 max-w-md">
                    {/* Current Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Password *
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
                            <input
                                type={showPassword.current ? "text" : "password"}
                                value={passwordData.currentPassword}
                                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                                required
                                className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                placeholder="Enter your current password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('current')}
                                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                            >
                                {showPassword.current ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                    </div>

                    {/* New Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Password *
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
                            <input
                                type={showPassword.new ? "text" : "password"}
                                value={passwordData.newPassword}
                                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                                required
                                className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                placeholder="Enter your new password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('new')}
                                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                            >
                                {showPassword.new ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            Password must be at least 8 characters long
                        </p>
                    </div>

                    {/* Confirm New Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm New Password *
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
                            <input
                                type={showPassword.confirm ? "text" : "password"}
                                value={passwordData.confirmPassword}
                                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                                required
                                className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                placeholder="Confirm your new password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('confirm')}
                                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                            >
                                {showPassword.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                            <p className="text-sm text-red-500 mt-1">
                                Passwords do not match
                            </p>
                        )}
                    </div>
                </div>

                {/* Password Action Buttons */}
                <div className="flex justify-start space-x-4 mt-8 pt-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={() => setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                        Clear
                    </button>
                    <button
                        type="submit"
                        className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg hover:from-red-600 hover:to-pink-600 transition-all duration-200 font-medium shadow-lg"
                    >
                        <Lock size={20} />
                        <span>Update Password</span>
                    </button>
                </div>
            </form>
        </div>
    );
}